import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ChangePropertyStatusModal from 'src/sections/dashboard-sections/ChangePropertyStatucModal';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Skeleton,
  Pagination,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterDrawer from 'src/sections/dashboard-sections/FilterDrawer';
import AddIcon from '@mui/icons-material/Add';
import PropertyViewModal from 'src/sections/dashboard-sections/PropertyViewModal';
import {
  getProperties,
  deleteProperty,
  updateProperty,
  markPropertyActive,
  saveBestProperty
} from 'src/ApiServices';
import { changeFeaturedStatus } from 'src/ApiServices';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BedIcon from '@mui/icons-material/Hotel';
import BathroomIcon from '@mui/icons-material/Bathtub';
import GarageIcon from '@mui/icons-material/Garage';
import CarIcon from '@mui/icons-material/DirectionsCar';
const formatPrice = (price) => {
  if (!price) return 'N/A';
  return `$${parseFloat(price).toLocaleString()}`;
};
export default function Properties() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  // State declarations
  const [properties, setProperties] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [bestModalOpen, setBestModalOpen] = useState(false);
  const [selectedPropertyForBest, setSelectedPropertyForBest] = useState(null);
  const [currentBestStatus, setCurrentBestStatus] = useState(false);
  const [bestLoading, setBestLoading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedPropertyForView, setSelectedPropertyForView] = useState(null);
  // Modal states
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPropertyForDelete, setSelectedPropertyForDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeModalOpen, setActiveModalOpen] = useState(false);
  const [selectedPropertyForActive, setSelectedPropertyForActive] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [currentActiveStatus, setCurrentActiveStatus] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);

  // Optimized table configuration with better width management
  const tableHeaders = [
    {
      name: 'Property Details',
      width: isSmallMobile ? '280px' : isMobile ? '300px' : '320px',
      minWidth: '280px',
      maxWidth: '320px'
    },
    {
      name: 'Specs',
      width: isSmallMobile ? '100px' : isMobile ? '120px' : '140px',
      minWidth: '100px',
      maxWidth: '140px'
    },
    {
      name: isMobile ? 'Metrics' : 'Property Metrics',
      width: isSmallMobile ? '140px' : isMobile ? '160px' : '180px',
      minWidth: '140px',
      maxWidth: '180px'
    },
    {
      name: 'Status',
      width: isSmallMobile ? '160px' : isMobile ? '180px' : '200px',
      minWidth: '160px',
      maxWidth: '200px'
    },
    {
      name: 'Actions',
      width: isSmallMobile ? '140px' : isMobile ? '160px' : '180px',
      minWidth: '140px',
      maxWidth: '180px'
    }
  ];

  // Memoized functions
  const getStatusColor = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'sold':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  }, []);

  const getAvailableForLabel = useCallback((propertyStatus) => {
    if (!propertyStatus || propertyStatus === '0') return 'N/A';
    switch (propertyStatus.toLowerCase()) {
      case 'sale':
        return 'Sale';
      case 'rent':
        return 'Rent';
      case 'sold':
        return 'Sold';
      default:
        return propertyStatus;
    }
  }, []);

  // Data fetching
  const fetchData = useCallback(async (currentPage, filters = {}) => {
    setLoading(true);
    try {
      const filterParams = {
        page: currentPage,
        ...(filters.priceRange && {
          min_price: filters.priceRange[0],
          max_price: filters.priceRange[1]
        }),
        ...(filters.type && { property_type_id: filters.type }),
        ...(filters.property_status && { status: filters.property_status }),
        ...(filters.buildYear && { year_built: filters.buildYear }),
        ...(filters.bedrooms && { bedrooms: filters.bedrooms }),
        ...(filters.availableFor && { property_status: filters.availableFor }),
        ...(filters.isFeatured && { is_featured: 1 }),
        ...(filters.bathrooms && { bathrooms: filters.bathrooms }),
        ...(filters.garage && { is_garage: 1 }),
        ...(filters.search && { search: filters.search })
      };

      const response = await getProperties(filterParams);

      const processedProperties = (response.data || []).map((property) => ({
        ...property,
        is_active: property.status?.toLowerCase() === 'active' ? 1 : 0
      }));

      setProperties(processedProperties);
      setPagination(
        response.pagination || {
          current_page: 1,
          per_page: 20,
          total: 0,
          last_page: 1,
          has_more_pages: false
        }
      );
    } catch (error) {
      console.error('Failed to load properties', error);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Effects
  useEffect(() => {
    fetchData(page, appliedFilters);
  }, [page, fetchData]);

  // Event handlers
  const handleAddProperty = () => navigate('/add-properties');

  const handleFilterClick = () => setFilterDrawerOpen(true);
  const handleCloseFilterDrawer = () => setFilterDrawerOpen(false);

  const handleApplyFilters = useCallback(
    async (filters) => {
      setPage(1);
      setSearchQuery('');
      setAppliedFilters(filters);
      await fetchData(1, filters);
    },
    [fetchData]
  );

  const handleSearchSubmit = useCallback(async () => {
    setPage(1);
    const searchFilters = {
      ...appliedFilters,
      ...(searchQuery && { search: searchQuery })
    };
    setAppliedFilters(searchFilters);
    await fetchData(1, searchFilters);
  }, [searchQuery, appliedFilters, fetchData]);

  const handleClearSearch = useCallback(async () => {
    setSearchQuery('');
    setPage(1);
    const filtersWithoutSearch = { ...appliedFilters };
    delete filtersWithoutSearch.search;
    setAppliedFilters(filtersWithoutSearch);
    await fetchData(1, filtersWithoutSearch);
  }, [appliedFilters, fetchData]);

  const handlePageChange = (event, value) => setPage(value);

  // Property actions
  const handleDeleteClick = (property) => {
    setSelectedPropertyForDelete(property);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPropertyForDelete) return;

    try {
      setDeleteLoading(true);
      await deleteProperty(selectedPropertyForDelete.id);
      toast.success('Property deleted successfully!');
      fetchData(page, appliedFilters);
      setDeleteModalOpen(false);
      setSelectedPropertyForDelete(null);
    } catch (error) {
      console.error('Failed to delete property:', error);
      toast.error(error.response?.data?.message || 'Failed to delete property!');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedPropertyForDelete(null);
  };

  const handleActiveClick = (property, currentStatus) => {
    setSelectedPropertyForActive(property);
    setCurrentActiveStatus(currentStatus);
    setActiveModalOpen(true);
  };

  const handleActiveConfirm = async () => {
    if (!selectedPropertyForActive) return;

    try {
      setActiveLoading(true);
      const formData = new FormData();
      formData.append('id', selectedPropertyForActive.id);
      formData.append('is_active', currentActiveStatus ? 0 : 1);

      await markPropertyActive(formData);
      toast.success(`Property ${currentActiveStatus ? 'deactivated' : 'activated'} successfully!`);
      await fetchData(page, appliedFilters);

      setActiveModalOpen(false);
      setSelectedPropertyForActive(null);
      setCurrentActiveStatus(false);
    } catch (error) {
      console.error('Failed to update active status:', error);

      // Check if it's a validation error with the specific structure
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Set the error data to display in modal
        setValidationErrors(error.response.data.errors);
        setShowValidationModal(true);
      } else {
        toast.error(
          error.response?.data?.message ||
            'Failed to update active status. Please fill all required fields'
        );
      }
    } finally {
      setActiveLoading(false);
    }
  };

  const handleActiveCancel = () => {
    setActiveModalOpen(false);
    setSelectedPropertyForActive(null);
    setCurrentActiveStatus(false);
  };

  const handleFeaturedToggle = async (propertyId, currentFeaturedStatus) => {
    const previousProperties = [...properties];
    setProperties((prevProperties) =>
      prevProperties.map((property) =>
        property.id === propertyId
          ? { ...property, is_featured: currentFeaturedStatus ? 0 : 1 }
          : property
      )
    );

    try {
      const newFeaturedStatus = currentFeaturedStatus ? 0 : 1;
      await changeFeaturedStatus(propertyId, newFeaturedStatus);
      toast.success(`Property ${currentFeaturedStatus ? 'removed from' : 'added to'} featured!`);
    } catch (error) {
      setProperties(previousProperties);
      console.error('Failed to update featured status:', error);
      // toast.error('Failed to update featured status');
      toast.error(error.response?.data?.message || 'Failed to update featured status');
    }
  };

  const handleBestToggle = (property) => {
    const propertyId = property.id;
    const currentBestStatus = property.is_best === 1;

    if (currentBestStatus) {
      performBestToggle(property, currentBestStatus);
      return;
    }

    const existingBestProperty = properties.find((p) => p.is_best === 1 && p.id !== propertyId);

    if (existingBestProperty) {
      setSelectedPropertyForBest(property);
      setCurrentBestStatus(currentBestStatus);
      setBestModalOpen(true);
    } else {
      performBestToggle(property, currentBestStatus);
    }
  };

  const performBestToggle = async (property, currentBestStatus) => {
    const propertyId = property.id;
    const newBestStatus = currentBestStatus ? 0 : 1;

    const previousProperties = [...properties];

    // Update local state immediately
    setProperties((prev) =>
      prev.map((p) => ({
        ...p,
        is_best: p.id === propertyId ? newBestStatus : newBestStatus ? 0 : p.is_best
      }))
    );

    try {
      await saveBestProperty({
        property_id: propertyId,
        is_best: newBestStatus
      });

      toast.success(`Property ${newBestStatus ? 'marked as' : 'removed from'} Best Property!`);

      // Refresh data to ensure sync with backend
      await fetchData(page, appliedFilters);
    } catch (error) {
      // Revert on error
      setProperties(previousProperties);
      toast.error('Failed to update Best Property status');
      console.error('Error saving best property:', error);
    }
  };

  const handleBestConfirm = async () => {
    if (!selectedPropertyForBest) return;

    try {
      setBestLoading(true);
      await performBestToggle(selectedPropertyForBest, currentBestStatus);
      setBestModalOpen(false);
      setSelectedPropertyForBest(null);
    } catch (error) {
      console.error('Failed to update best property:', error);
    } finally {
      setBestLoading(false);
    }
  };

  const handleBestCancel = () => {
    setBestModalOpen(false);
    setSelectedPropertyForBest(null);
    setCurrentBestStatus(false);
  };

  const handleAvailableForClick = (property) => {
    setSelectedProperty(property);
    setModalOpen(true);
  };

  // ADD VIEW MODAL HANDLER
  const handleViewClick = (property) => {
    setSelectedPropertyForView(property);
    setViewModalOpen(true);
  };

  const handleViewClose = () => {
    setViewModalOpen(false);
    setSelectedPropertyForView(null);
  };

  // UI rendering helpers
  const renderSkeletonRows = () =>
    Array.from({ length: pagination.per_page || 10 }).map((_, index) => (
      <TableRow key={index}>
        {tableHeaders.map((_, cellIndex) => (
          <TableCell key={cellIndex} sx={{ px: isSmallMobile ? 1 : 2 }}>
            <Skeleton variant="text" height={40} />
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderNoRecordsFound = () => (
    <TableRow>
      <TableCell
        colSpan={tableHeaders.length}
        sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{ color: palette.text.secondary, fontFamily: 'poppinsMedium', mb: 1 }}
          >
            No Properties Found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: palette.grey[600],
              fontFamily: 'poppinsRegular',
              maxWidth: 400,
              textAlign: 'center'
            }}
          >
            {Object.keys(appliedFilters).length > 0
              ? 'No properties match your current filters. Try adjusting your search criteria.'
              : 'No properties available. Add a new property to get started.'}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  // Column 1: Property Details - Optimized for text overflow
  const renderPropertyDetails = (property) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isSmallMobile ? 1 : 1.5,
        width: '100%',
        maxWidth: '320px',
        minWidth: '280px'
      }}
    >
      {/* Property Image*/}
      <Box
        sx={{
          width: isSmallMobile ? 70 : isMobile ? 90 : 100,
          height: isSmallMobile ? 70 : isMobile ? 90 : 100,
          borderRadius: 1,
          overflow: 'hidden',
          flexShrink: 0,
          backgroundColor: palette.grey[200]
        }}
      >
        <img
          src={
            property?.images && property?.images[0]
              ? property?.images[0]
              : 'https://estateapi.dotserviz.co/storage/property_images/property-placeholder.jpg'
          }
          alt={property?.title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          onError={(e) => {
            e.target.src =
              'https://estateapi.dotserviz.co/storage/property_images/property-placeholder.jpg';
          }}
        />
      </Box>
      {/* Property Info */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden'
        }}
      >
        <Tooltip title={property?.title} arrow>
          <Typography
            sx={{
              fontFamily: 'poppinsMedium',
              fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '13px',
              color: palette.text.primary,
              lineHeight: 1.3,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}
          >
            {property?.title || 'N/A'}
          </Typography>
        </Tooltip>

        <Tooltip title={property?.address} arrow>
          <Typography
            sx={{
              fontSize: isSmallMobile ? '11px' : isMobile ? '14px' : '11px',
              color: palette.text.secondary,
              lineHeight: 1.3,
              mb: 0.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}
          >
            {property?.address?.replace(/\r\n/g, ', ') || 'N/A'}
          </Typography>
        </Tooltip>

        <Typography
          sx={{
            fontSize: isSmallMobile ? '10px' : isMobile ? '14px' : '10px',
            color: palette.text.disabled,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {property?.city || 'N/A'} | {property?.state || 'N/A'}
        </Typography>

        {/* Price */}
        <Typography
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.primary.main,
            fontSize: isSmallMobile ? '12px' : '13px',
            mt: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {formatPrice(property.price)}
          {['1', 1].includes(property?.is_area_unit) && (
            <> / {property?.area_unit?.name || 'unit'}</>
          )}
        </Typography>
      </Box>
    </Box>
  );

  // Column 2: Specs - Compact layout
  const renderSpecs = (property) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        width: '100%',
        maxWidth: '140px',
        minWidth: '100px'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <BedIcon sx={{ fontSize: isSmallMobile ? 12 : 14, color: palette.text.secondary }} />
        <Typography
          sx={{
            fontSize: isSmallMobile ? '11px' : '12px',
            fontFamily: 'poppinsRegular',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {property.bedrooms || 0} Beds
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <BathroomIcon sx={{ fontSize: isSmallMobile ? 12 : 14, color: palette.text.secondary }} />
        <Typography
          sx={{
            fontSize: isSmallMobile ? '11px' : '12px',
            fontFamily: 'poppinsRegular',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {property.bathrooms || 0} Baths
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <GarageIcon sx={{ fontSize: isSmallMobile ? 12 : 14, color: palette.text.secondary }} />
        <Typography
          sx={{
            fontSize: isSmallMobile ? '11px' : '12px',
            fontFamily: 'poppinsRegular',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          Garage: {property.is_garage == 1 ? 'Yes' : property.is_garage == 0 ? 'No' : 'N/A'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CarIcon sx={{ fontSize: isSmallMobile ? 12 : 14, color: palette.text.secondary }} />
        <Typography
          sx={{
            fontSize: isSmallMobile ? '11px' : '12px',
            fontFamily: 'poppinsRegular',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {property.garage_cars || 'N/A'} Cars
        </Typography>
      </Box>
    </Box>
  );

  // Column 3: Property Metrics - Compact layout
  const renderPropertyMetrics = (property) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        width: '100%',
        maxWidth: '180px',
        minWidth: '140px'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: isSmallMobile ? '10px' : '11px',
            color: palette.text.secondary,
            fontFamily: 'poppinsMedium',
            minWidth: isSmallMobile ? '55px' : '65px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          Build Year:
        </Typography>
        <Typography
          sx={{
            fontSize: isSmallMobile ? '11px' : '12px',
            fontFamily: 'poppinsRegular',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {property.year_built || 'N/A'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: isSmallMobile ? '10px' : '11px',
            color: palette.text.secondary,
            fontFamily: 'poppinsMedium',
            minWidth: isSmallMobile ? '55px' : '65px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          Area Unit:
        </Typography>
        <Typography
          sx={{
            fontSize: isSmallMobile ? '11px' : '12px',
            fontFamily: 'poppinsRegular',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {property.area_unit?.name || 'N/A'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: isSmallMobile ? '10px' : '11px',
            color: palette.text.secondary,
            fontFamily: 'poppinsMedium',
            minWidth: isSmallMobile ? '55px' : '65px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          Area Size:
        </Typography>
        <Typography
          sx={{
            fontSize: isSmallMobile ? '11px' : '12px',
            fontFamily: 'poppinsRegular',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {property.area_size ? property.area_size.toLocaleString() : 'N/A'}
        </Typography>
      </Box>
    </Box>
  );

  // Column 4: Status - Compact layout
  const renderStatus = (property) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        width: '100%',
        maxWidth: '200px',
        minWidth: '160px'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: isSmallMobile ? '10px' : '11px',
            color: palette.text.secondary,
            fontFamily: 'poppinsMedium',
            minWidth: isSmallMobile ? '60px' : '70px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {isSmallMobile ? 'Listing:' : 'Listing Status:'}
        </Typography>
        <Chip
          label={property.status || 'N/A'}
          color={getStatusColor(property.status)}
          size="small"
          sx={{
            fontFamily: 'poppinsMedium',
            fontSize: isSmallMobile ? '9px' : '10px',
            height: isSmallMobile ? 20 : 24,
            minWidth: isSmallMobile ? '50px' : '60px',
            maxWidth: '80px'
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: isSmallMobile ? '10px' : '11px',
            color: palette.text.secondary,
            fontFamily: 'poppinsMedium',
            minWidth: isSmallMobile ? '60px' : '70px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {isSmallMobile ? 'Prop Status:' : 'Property Status:'}
        </Typography>
        <Typography
          sx={{
            fontSize: isSmallMobile ? '11px' : '12px',
            fontFamily: 'poppinsRegular',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {getAvailableForLabel(property.property_status)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Property Type">
          <Typography
            sx={{
              fontSize: isSmallMobile ? '10px' : '11px',
              color: palette.text.secondary,
              fontFamily: 'poppinsMedium',
              minWidth: isSmallMobile ? '60px' : '70px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {isSmallMobile ? 'Type:' : 'Property Type:'}
          </Typography>
        </Tooltip>

        <Tooltip title={property.property_type?.name || 'N/A'}>
          <Typography
            sx={{
              fontSize: isSmallMobile ? '11px' : '12px',
              fontFamily: 'poppinsRegular',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {property.property_type?.name || 'N/A'}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );

  // Column 5: Actions - Compact layout
  const renderActions = (property) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
        maxWidth: '180px',
        minWidth: '140px'
      }}
    >
      {/* Toggles Row */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}
        >
          <Typography
            sx={{
              fontSize: isSmallMobile ? '10px' : '11px',
              fontFamily: 'poppinsMedium',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            Featured
          </Typography>
          <Switch
            checked={property.is_featured === 1}
            onChange={() => handleFeaturedToggle(property.id, property.is_featured === 1)}
            color="primary"
            size="small"
            disabled={property.status?.toLowerCase() !== 'active'}
          />
        </Box>

        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}
        >
          <Typography
            sx={{
              fontSize: isSmallMobile ? '10px' : '11px',
              fontFamily: 'poppinsMedium',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {isSmallMobile ? 'Best' : 'Mark as Best'}
          </Typography>
          <Switch
            checked={property.is_best === 1}
            Frequently
            Asked
            Questions
            onChange={() => handleBestToggle(property)}
            disabled={property.status?.toLowerCase() !== 'active'}
            color="primary"
            size="small"
          />
        </Box>

        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}
        >
          <Typography
            sx={{
              fontSize: isSmallMobile ? '10px' : '11px',
              fontFamily: 'poppinsMedium',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {isSmallMobile ? 'Active' : 'Mark as Active'}
          </Typography>
          <Switch
            checked={property.status?.toLowerCase() === 'active'}
            onChange={() =>
              handleActiveClick(property, property.status?.toLowerCase() === 'active')
            }
            color="primary"
            size="small"
          />
        </Box>
      </Box>

      {/* Action Icons Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, px: 0.5 }}>
        <Tooltip title="View Property Details">
          <IconButton
            size="small"
            onClick={() => handleViewClick(property)}
            sx={{ color: palette.info.main }}
          >
            <ViewIcon fontSize={isSmallMobile ? 'small' : 'small'} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Edit Property">
          <IconButton size="small" onClick={() => navigate(`/add-properties?edit=${property.id}`)}>
            <EditIcon fontSize={isSmallMobile ? 'small' : 'small'} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete Property">
          <IconButton size="small" onClick={() => handleDeleteClick(property)}>
            <DeleteIcon
              fontSize={isSmallMobile ? 'small' : 'small'}
              sx={{ color: palette.error.main }}
            />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Page title={`Manage Properties | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, overflow: 'hidden' }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isSmallMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isSmallMobile ? 'flex-start' : 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            gap: isSmallMobile ? 2 : 0
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 18, sm: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark,
              whiteSpace: 'nowrap'
            }}
          >
            Manage Properties
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: isSmallMobile ? '100%' : 'auto',
              justifyContent: isSmallMobile ? 'space-between' : 'flex-end'
            }}
          >
            <Button
              variant="contained"
              sx={{
                background: palette.primary.main,
                color: palette.primary.contrastText,
                height: 40,
                minWidth: 'auto',
                fontSize: isSmallMobile ? '12px' : '14px',
                px: isSmallMobile ? 1.5 : 2,
                whiteSpace: 'nowrap',
                '&:hover': { background: palette.primary.dark }
              }}
              onClick={handleFilterClick}
              startIcon={!isSmallMobile && <FilterListIcon />}
            >
              {isSmallMobile ? <FilterListIcon fontSize="small" /> : 'Filter'}
            </Button>

            <FilterDrawer
              open={filterDrawerOpen}
              onClose={handleCloseFilterDrawer}
              onApplyFilters={handleApplyFilters}
            />

            <Button
              variant="contained"
              sx={{
                background: palette.primary.main,
                color: palette.primary.contrastText,
                height: 40,
                minWidth: 'auto',
                fontSize: isSmallMobile ? '12px' : '14px',
                px: isSmallMobile ? 1.5 : 2,
                whiteSpace: 'nowrap',
                '&:hover': { background: palette.primary.dark }
              }}
              onClick={handleAddProperty}
              startIcon={!isSmallMobile && <AddIcon />}
            >
              {isSmallMobile ? <AddIcon fontSize="small" /> : 'Add Property'}
            </Button>
          </Box>
        </Box>

        {/* Search Section */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={10} lg={11}>
              <TextField
                variant="outlined"
                placeholder="Search by title, description, address..."
                size="medium"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch} edge="end">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.divider },
                    '&:hover fieldset': { borderColor: theme.palette.primary.main },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2} lg={1}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSearchSubmit}
                  fullWidth
                  sx={{
                    height: { xs: 48, md: 56 },
                    fontSize: { xs: '12px', md: '14px' },
                    fontFamily: 'poppinsMedium',
                    whiteSpace: 'nowrap'
                  }}
                  startIcon={isSmallMobile ? <SearchIcon /> : null}
                >
                  {isSmallMobile ? '' : 'Search'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Properties Table */}
        <Card sx={{ overflow: 'hidden', width: '100%' }}>
          <CardContent sx={{ p: 0, width: '100%' }}>
            <TableContainer
              sx={{
                width: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                '&::-webkit-scrollbar': {
                  height: 8,
                  width: 8
                },
                '&::-webkit-scrollbar-track': {
                  background: palette.grey[200],
                  borderRadius: 4
                },
                '&::-webkit-scrollbar-thumb': {
                  background: palette.grey[400],
                  borderRadius: 4,
                  '&:hover': {
                    background: palette.grey[500]
                  }
                }
              }}
            >
              <Table
                sx={{
                  minWidth: '1020px', // Reduced from previous values
                  width: '100%',
                  tableLayout: 'fixed'
                }}
              >
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          whiteSpace: 'nowrap',
                          px: isSmallMobile ? 1 : 2,
                          py: 2,
                          backgroundColor: theme.palette.background.paper,
                          fontFamily: 'poppinsSemiBold',
                          fontSize: isSmallMobile ? '12px' : '14px',
                          borderBottom: `2px solid ${palette.divider}`,
                          width: header.width,
                          minWidth: header.minWidth,
                          maxWidth: header.maxWidth,
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {header.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading
                    ? renderSkeletonRows()
                    : properties.length > 0
                    ? properties.map((property) => (
                        <TableRow
                          key={property.id}
                          sx={{
                            '&:hover': { backgroundColor: palette.action.hover },
                            '& > td': {
                              px: isSmallMobile ? 1 : 2,
                              py: 1.5,
                              verticalAlign: 'top',
                              width: tableHeaders.find((_, i) => i === 0)?.width,
                              minWidth: tableHeaders.find((_, i) => i === 0)?.minWidth,
                              maxWidth: tableHeaders.find((_, i) => i === 0)?.maxWidth,
                              overflow: 'hidden'
                            }
                          }}
                        >
                          {/* Column 1: Property Details */}
                          <TableCell>{renderPropertyDetails(property)}</TableCell>

                          {/* Column 2: Specs */}
                          <TableCell>{renderSpecs(property)}</TableCell>

                          {/* Column 3: Property Metrics */}
                          <TableCell>{renderPropertyMetrics(property)}</TableCell>

                          {/* Column 4: Status */}
                          <TableCell>{renderStatus(property)}</TableCell>

                          {/* Column 5: Actions */}
                          <TableCell>{renderActions(property)}</TableCell>
                        </TableRow>
                      ))
                    : renderNoRecordsFound()}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Pagination */}
        {!loading && properties.length > 0 && pagination.total > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: isSmallMobile ? 'center' : 'flex-end',
              mt: 3,
              overflow: 'hidden'
            }}
          >
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
              size={isSmallMobile ? 'small' : 'medium'}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: isSmallMobile ? '12px' : '14px'
                }
              }}
            />
          </Box>
        )}

        <PropertyViewModal
          open={viewModalOpen}
          onClose={handleViewClose}
          property={selectedPropertyForView}
        />

        {/* Modals */}
        <ChangePropertyStatusModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          property={selectedProperty}
          onSave={() => fetchData(page, appliedFilters)}
        />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1,
              width: isSmallMobile ? '95%' : 'auto',
              m: isSmallMobile ? 1 : 0,
              maxWidth: isSmallMobile ? 'none' : 500
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3, px: isSmallMobile ? 2 : 3 }}>
            <WarningAmberIcon
              sx={{ fontSize: isSmallMobile ? 50 : 60, color: palette.warning.main, mb: 2 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'poppinsSemiBold',
                color: palette.text.primary,
                fontSize: isSmallMobile ? '1.1rem' : '1.25rem'
              }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2, px: isSmallMobile ? 2 : 3 }}>
            <DialogContentText
              sx={{
                color: palette.text.secondary,
                fontFamily: 'poppinsRegular',
                fontSize: isSmallMobile ? '0.9rem' : '1rem'
              }}
            >
              Are you sure you want to delete <strong>{selectedPropertyForDelete?.title}</strong>?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'center',
              gap: 2,
              pb: 3,
              px: isSmallMobile ? 2 : 3,
              flexDirection: isSmallMobile ? 'column' : 'row'
            }}
          >
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              disabled={deleteLoading}
              fullWidth={isSmallMobile}
              sx={{ minWidth: isSmallMobile ? 'auto' : 100 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              disabled={deleteLoading}
              fullWidth={isSmallMobile}
              sx={{ minWidth: isSmallMobile ? 'auto' : 100 }}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Active/Inactive Confirmation Modal */}
        <Dialog
          open={activeModalOpen}
          onClose={handleActiveCancel}
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1,
              width: isSmallMobile ? '95%' : 'auto',
              m: isSmallMobile ? 1 : 0,
              maxWidth: isSmallMobile ? 'none' : 500
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3, px: isSmallMobile ? 2 : 3 }}>
            <WarningAmberIcon
              sx={{ fontSize: isSmallMobile ? 50 : 60, color: palette.warning.main, mb: 2 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'poppinsSemiBold',
                color: palette.text.primary,
                fontSize: isSmallMobile ? '1.1rem' : '1.25rem'
              }}
            >
              {currentActiveStatus ? 'Confirm Deactivation' : 'Confirm Activation'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2, px: isSmallMobile ? 2 : 3 }}>
            <DialogContentText
              sx={{
                color: palette.text.secondary,
                fontFamily: 'poppinsRegular',
                fontSize: isSmallMobile ? '0.9rem' : '1rem'
              }}
            >
              Are you sure you want to {currentActiveStatus ? 'deactivate' : 'activate'}{' '}
              <strong>{selectedPropertyForActive?.title}</strong>?
              <br />
              {currentActiveStatus
                ? 'The property will be hidden from the website.'
                : 'The property will be visible on the website.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'center',
              gap: 2,
              pb: 3,
              px: isSmallMobile ? 2 : 3,
              flexDirection: isSmallMobile ? 'column' : 'row'
            }}
          >
            <Button
              onClick={handleActiveCancel}
              variant="outlined"
              disabled={activeLoading}
              fullWidth={isSmallMobile}
              sx={{ minWidth: isSmallMobile ? 'auto' : 100 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleActiveConfirm}
              variant="contained"
              disabled={activeLoading}
              fullWidth={isSmallMobile}
              sx={{ minWidth: isSmallMobile ? 'auto' : 100 }}
            >
              {activeLoading ? 'Processing...' : currentActiveStatus ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Best Property Modal  */}
        <Dialog
          open={bestModalOpen}
          onClose={handleBestCancel}
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1,
              width: isSmallMobile ? '95%' : 'auto',
              m: isSmallMobile ? 1 : 0,
              maxWidth: isSmallMobile ? 'none' : 800
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3, px: isSmallMobile ? 2 : 3 }}>
            <WarningAmberIcon
              sx={{ fontSize: isSmallMobile ? 50 : 60, color: palette.warning.main, mb: 2 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'poppinsSemiBold',
                color: palette.text.primary,
                fontSize: isSmallMobile ? '1.1rem' : '1.25rem'
              }}
            >
              Change Best Property
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2, px: isSmallMobile ? 2 : 3 }}>
            <DialogContentText
              sx={{
                color: palette.text.secondary,
                fontFamily: 'poppinsRegular',
                fontSize: isSmallMobile ? '0.9rem' : '1rem'
              }}
            >
              Are you sure you want to mark <strong>{selectedPropertyForBest?.title}</strong> as
              Best Property?
              <br />
              The current best property will be automatically replaced.
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'center',
              gap: 2,
              pb: 3,
              px: isSmallMobile ? 2 : 3,
              flexDirection: isSmallMobile ? 'column' : 'row'
            }}
          >
            <Button
              onClick={handleBestCancel}
              variant="outlined"
              disabled={bestLoading}
              fullWidth={isSmallMobile}
              sx={{ minWidth: isSmallMobile ? 'auto' : 100 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBestConfirm}
              variant="contained"
              disabled={bestLoading}
              fullWidth={isSmallMobile}
              sx={{ minWidth: isSmallMobile ? 'auto' : 100 }}
            >
              {bestLoading ? 'Updating...' : 'Mark as Best'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Validation Errors Modal */}
        <Dialog
          open={showValidationModal}
          onClose={() => setShowValidationModal(false)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              width: isSmallMobile ? '95%' : 'auto',
              m: isSmallMobile ? 1 : 0,
              maxWidth: isSmallMobile ? 'none' : 500,
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <DialogTitle
            sx={{
              textAlign: 'center',
              py: 3,
              px: isSmallMobile ? 2 : 3,
              flexShrink: 0
            }}
          >
            <WarningAmberIcon
              sx={{ fontSize: isSmallMobile ? 50 : 60, color: palette.warning.main, mb: 2 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'poppinsSemiBold',
                color: palette.text.primary,
                fontSize: isSmallMobile ? '1.1rem' : '1.25rem'
              }}
            >
              Cannot Activate Property
            </Typography>
          </DialogTitle>

          <DialogContent
            sx={{
              pb: 2,
              px: isSmallMobile ? 2 : 3,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <DialogContentText
              sx={{
                color: palette.text.secondary,
                fontFamily: 'poppinsRegular',
                fontSize: isSmallMobile ? '0.9rem' : '1rem',
                mb: 2,
                flexShrink: 0
              }}
            >
              Property cannot be activated due to missing fields:
            </DialogContentText>

            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                border: `1px solid ${palette.grey[300]}`,
                borderRadius: 1,
                p: 2,
                backgroundColor: palette.grey[50],
                minHeight: 100
              }}
            >
              {validationErrors.map((error, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1,
                    '&:last-child': { mb: 0 }
                  }}
                >
                  <Typography
                    sx={{
                      color: palette.error.main,
                      fontSize: '0.8rem',
                      fontFamily: 'poppinsRegular',
                      lineHeight: 1.4
                    }}
                  >
                    • {error}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              justifyContent: 'end',
              gap: 2,
              py: 3,
              px: isSmallMobile ? 2 : 3,
              flexDirection: isSmallMobile ? 'column' : 'row',
              flexShrink: 0
            }}
          >
            <Button
              onClick={() => {
                setShowValidationModal(false);
                setValidationErrors([]);
              }}
              variant="contained"
              fullWidth={isSmallMobile}
              sx={{ minWidth: isSmallMobile ? 'auto' : 120 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
//
